import React from 'react';
import PropTypes from 'prop-types';

import CheckCircle from '@mui/icons-material/CheckCircle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import { styled } from '@mui/system';

import {
  getDateTimeFromFulfillmentDateAndTime,
  getFulfillmentDateDisplay
} from 'catering/helpers/dates';
import { slugify } from 'helpers/slugify';
import {
  DATE_DISPLAY_FORMAT_NO_COMMA,
  DATE_DISPLAY_FORMAT_NO_COMMA_WITH_YEAR
} from 'catering/constants/dates';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import getDisabledSelectedTextColor from 'styles/helpers/getDisabledSelectedTextColor';
import {
  getButtonBackgroundHoverColor,
  getSectionNavColor,
  getSectionNavMediumColor,
  getItemDescriptionsAndPricesTypography
} from 'styles/helpers/getThemeStyles';

const StyledButton = styled(Button)(({ disabled }) => ({
  paddingLeft: 12,
  paddingRight: 12,
  cursor: 'pointer',
  pointerEvents: disabled && 'none',
  textTransform: 'none',
  textAlign: 'left',
  letterSpacing: 'initial'
}));

const InnerBox = styled(Box, {
  shouldForwardProp: muiPropForwarding('isLast')
})(({ theme, isLast }) => ({
  width: '100%',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  borderBottom: isLast ? '' : `1px solid ${theme.palette.borders.primary}`,
  '&:hover *': {
    color: theme.palette.secondary.main
  }
}));

const Content = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
});

const Title = styled(Typography)(({ theme, selected, disabled }) => {
  let fontStyles = {
    fontWeight: 'normal',
    fontFamily: theme.typography.fontFamily
  };
  let color = getDisabledSelectedTextColor({ theme, selected, disabled });
  if (selected) {
    color = getButtonBackgroundHoverColor(theme);
    fontStyles = getItemDescriptionsAndPricesTypography(theme);
  } else {
    fontStyles = getItemDescriptionsAndPricesTypography(theme);
    color = getSectionNavColor(theme);
  }
  return {
    color,
    ...fontStyles
  };
});

const StyledDate = styled(Typography)(({ theme, disabled }) => {
  let color = '';
  let fontStyles = {};

  if (disabled) {
    color = theme.palette.text.disabled;
  }
  fontStyles = getItemDescriptionsAndPricesTypography(theme);
  color = getSectionNavMediumColor(theme);
  return {
    fontSize: 12,
    color,
    ...fontStyles
  };
});

const StyledCheckCircle = styled(CheckCircle)(({ theme }) => ({
  fill: getButtonBackgroundHoverColor(theme)
}));

const DEFAULT_TIME = '00:00:00';
const LocationMenuCard = ({
  menuName,
  startDate,
  startTime,
  isLast,
  selected = false,
  handleDateTimeChange,
  menuId
}) => {
  const disabled = !startDate;

  const formattedDateTime = getDateTimeFromFulfillmentDateAndTime(
    startDate,
    DEFAULT_TIME
  );

  const getFormattedDate = () => {
    const currentYear = new Date().getFullYear();

    if (formattedDateTime.getFullYear() !== currentYear) {
      return getFulfillmentDateDisplay(
        formattedDateTime,
        DATE_DISPLAY_FORMAT_NO_COMMA_WITH_YEAR
      );
    }

    return getFulfillmentDateDisplay(
      formattedDateTime,
      DATE_DISPLAY_FORMAT_NO_COMMA
    );
  };

  const formattedDate = getFormattedDate();

  const dateAvailableText = disabled
    ? `(ordering closed)`
    : `${formattedDate} (next available)`;
  return (
    <StyledButton
      variant="text"
      disabled={disabled}
      component={ListItem}
      width="100%"
      data-testid={`${slugify(menuName)}-list-item`}
      onClick={() => handleDateTimeChange(formattedDateTime, startTime, menuId)}
    >
      <InnerBox isLast={isLast}>
        <Content>
          <Box>
            <Title selected={selected} disabled={disabled}>
              {menuName}
            </Title>
            <StyledDate disabled={disabled}>{dateAvailableText}</StyledDate>
          </Box>
          {selected && <StyledCheckCircle data-testid="check-icon" />}
        </Content>
      </InnerBox>
    </StyledButton>
  );
};

LocationMenuCard.propTypes = {
  menuName: PropTypes.string.isRequired,
  menuId: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  startTime: PropTypes.string.isRequired,
  isLast: PropTypes.bool,
  selected: PropTypes.bool,
  handleDateTimeChange: PropTypes.func.isRequired
};

export default LocationMenuCard;
